import { useQuery } from '@tanstack/react-query';
import Config from 'src/common/Config';
import type { BotStatus, CalendarEvent } from 'src/meetings/models';

const API_URL = Config.getApiUrl();

const LIVE_POLL_INTERVAL_MS = 10_000;
const POLL_FROM_BEFORE_START_MS = 10 * 60_000;
const MAX_WAIT_BETWEEN_POLLS_MS = 60 * 60_000;

type MeetingTimes = Pick<CalendarEvent, 'scheduledStartAt' | 'scheduledEndAt'>;

type BotStatusResponse = {
  status: BotStatus;
  joinsAt: string | null;
};

type UseBotStatusResult = {
  botStatus: BotStatus | undefined;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
};

const refetchIntervalFrom = (pollFrom: number): number => {
  const now = Date.now();
  if (now >= pollFrom) {
    return LIVE_POLL_INTERVAL_MS;
  }
  // Wake up once polling is due, rather than polling all the way up to it.
  return Math.min(pollFrom - now, MAX_WAIT_BETWEEN_POLLS_MS);
};

const refetchIntervalFor = (
  botStatus: BotStatusResponse | undefined,
  meeting: MeetingTimes | null,
): number | false => {
  if (botStatus?.status === 'ACTIVE') {
    return LIVE_POLL_INTERVAL_MS;
  }
  // "Add bot" schedules the bot to join straight away, which can be well outside the meeting's times.
  if (botStatus?.status === 'SCHEDULED' && botStatus.joinsAt) {
    return refetchIntervalFrom(new Date(botStatus.joinsAt).getTime());
  }
  if (!meeting) {
    return false;
  }
  if (Date.now() > new Date(meeting.scheduledEndAt).getTime()) {
    return false;
  }
  return refetchIntervalFrom(
    new Date(meeting.scheduledStartAt).getTime() - POLL_FROM_BEFORE_START_MS,
  );
};

export const useBotStatus = (eventId: string, meeting: MeetingTimes | null): UseBotStatusResult => {
  const { data, isLoading, error, refetch } = useQuery<BotStatusResponse>({
    queryKey: ['botStatus', eventId],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/calendar-events/${eventId}/bot-status`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bot status');
      }

      return response.json();
    },
    refetchInterval: query => refetchIntervalFor(query.state.data, meeting),
    refetchOnWindowFocus: true,
  });

  return {
    botStatus: data?.status,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};
